import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { TrustTaxCategory } from '../../../shared/types/TrustTaxCategory';
import { Constants } from '../../../shared/utilities/Constants';
import { selectTrustTaxCategoriesFromInquiry, selectTrustTaxCodesFromInquiry } from '../../../transaction-view/reducers';
import { PtrsInfo } from '../../models';
import { 
  selectIsFromEditSubmittedTransaction, 
  selectPtrsInfo, 
  selectTrustIncomePercent, 
  selectTrustPrincipalPercent, 
  selectTrustTaxCategories, 
  selectTrustTaxCodes, 
  selectITAvailableCashMMF,
  selectITAvailableCashMargin, 
  selectTrustIncomeToPercent,
  selectTrustPrincipalToPercent,
  selectTransferType
} from '../../reducers';
import { currencyStringToNumber } from '../../utilities/currencyStringToNumber';
import { DrupalFormService } from '../../../shared/services/DrupalForm.service';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';

@Component({
  standalone: false,
  selector: 'mm-it-multiple-trust-fields',
  templateUrl: './it-multiple-trust-fields.component.html',
  styleUrls: ['./it-multiple-trust-fields.component.scss']
})
export class ItMultipleTrustFieldsComponent implements OnInit, OnDestroy {
  @Input() parentForm: UntypedFormGroup;
  @Input() datePickerControlName: string;
  @Input() displayToTrustFields: Boolean = false;
  @Input() displayFromTrustFields: Boolean = false;
  @Input() isEdit: boolean = false;
  noPaidFor: boolean; 
  destroy$: Subject<boolean> = new Subject();
  taxCategories$: Observable<TrustTaxCategory[]>;
  taxCodes$: Observable<string[]>;
  trustIncomePercent$: Observable<string>;
  trustPrincipalPercent$: Observable<string>;
  trustIncomeToPercent$: Observable<string>;
  trustPrincipalToPercent$: Observable<string>;
  ptrsInfo$: Observable<PtrsInfo>;
  availableCashMargin: string;
  availableCashMMF: string;
  transferType: string;
  readonly trustReceivingAccountCopy = Constants.trustReceivingAccountCopy;
  fromAccountControlNames = {
    trustIncomePercent: 'trustIncomePercent',
    trustPrincipalPercent: 'trustPrincipalPercent'
  };
  
  constructor(
    private store: Store,   
    private drupalFormService: DrupalFormService,
    private featureFlagService: FeatureFlagService) { }

  ngOnInit(): void {
    if (this.displayFromTrustFields) {
      this.trustIncomePercent$ = this.store.select(selectTrustIncomePercent)
      this.trustPrincipalPercent$ = this.store.select(selectTrustPrincipalPercent)

      this.store.select(selectIsFromEditSubmittedTransaction).pipe(takeUntil(this.destroy$)).subscribe((isFromEditSubmittedTransaction) => {
        if (isFromEditSubmittedTransaction) {
          this.taxCategories$ = this.store.pipe(select(selectTrustTaxCategoriesFromInquiry));
          this.taxCodes$ = this.store.pipe(select(selectTrustTaxCodesFromInquiry));
        } else {
          this.taxCategories$ = this.store.pipe(select(selectTrustTaxCategories));
          this.taxCodes$ = this.store.pipe(select(selectTrustTaxCodes));
        }
      });
    }

    this.ptrsInfo$ = this.store.select(selectPtrsInfo)

    this.ptrsInfo$
    .pipe(takeUntil(this.destroy$))
    .subscribe((ptrsInfo) => {
      if (ptrsInfo.hasError || ptrsInfo.paidFor.hasError || (!ptrsInfo.paidFor.individuals[0]?.name && !ptrsInfo.loading)) {
        this.noPaidFor = true;
      }
    })

    if (this.displayToTrustFields) {
      this.trustIncomeToPercent$ = this.store.select(selectTrustIncomeToPercent);
      this.trustPrincipalToPercent$ = this.store.select(selectTrustPrincipalToPercent);
    }

    this.store.select(selectITAvailableCashMMF).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.availableCashMMF = info;
    });

    this.store.select(selectITAvailableCashMargin).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.availableCashMargin = info;
    });

    this.store.select(selectTransferType).pipe(takeUntil(this.destroy$)).subscribe((transferType => {
      this.transferType = (typeof transferType === 'string') ? transferType: "";
    }));
  }

  get availableCash(): number {
    return currencyStringToNumber(this.availableCashMargin) + currencyStringToNumber(this.availableCashMMF);
  }

  get overdraftRulesFutureDate(): Date {
    let today = new Date(new Date().setHours(0, 0, 0, 0));
    return new Date(today.setDate(today.getDate() + 1));
  }

  ngOnDestroy(): void {
    this.parentForm?.removeControl(this.fromAccountControlNames.trustIncomePercent);
    this.parentForm?.removeControl(this.fromAccountControlNames.trustPrincipalPercent);
    this.destroy$.next(true);
    this.destroy$.unsubscribe();  
  }

  getDrupalPdf(name: string) {
    this.drupalFormService.createDrupalForm(name);
  }
}
